import {
    MouseEvent, UIEvent, useCallback, useMemo,
    useRef,
} from 'react';
import { Calendar } from 'data/firestore/agent/calendar/types';
import { Calendarevent } from 'data/firestore/agent/calendarevent/types';
import { CalendarEventClick } from 'types';
import { DIVIDER } from '../../../constants';
import getMinAvailableStartAt from '../helpers/getMinAvailableStartAt';
import useScrollToMinAvailableTime from '../hooks/useScrollToMinAvailableTime';
import CalendarItem from './calendar';
import ClockLine from './clockLine';
import {
    Container,
    StyledCalendars,
    Grid,
    GridLine,
    Content,
    Scroll,
} from './styled';

export interface CalendarViewColumn {
    id: string;
    events: Calendarevent[];
    availability: Calendar['availability'];
    color: string;
    getEventColor?: (event: Calendarevent) => string;
    calendar?: Calendar;
}

const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

interface Params {
    date?: Date;
    selectedEvent?: string | null;
    onScroll: (e: UIEvent<HTMLDivElement>) => void,
    onEventClick?: CalendarEventClick;
    onAvailableSpaceClick: (calendar: Calendar, e: MouseEvent<HTMLDivElement>) => void;
    calendars?: Calendar[],
    events?: Calendarevent[],
    columns?: CalendarViewColumn[],
    hideClockLine?: boolean;
    timeZone: string;
}

const Calendars = ({
    date = new Date(),
    selectedEvent,
    calendars = [],
    events = [],
    columns,
    onScroll = () => {},
    onEventClick = () => {},
    onAvailableSpaceClick = () => {},
    hideClockLine = false,
    timeZone,
}: Params) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const handleAvailableSpaceClick = useCallback(
        (calendar: Calendar) => (
            e: MouseEvent<HTMLDivElement>,
        ) => onAvailableSpaceClick(calendar, e),
        [onAvailableSpaceClick],
    );

    const weekDay = weekDays[date.getDay()];

    const visibleAvailability = useMemo(
        () => {
            if (columns) {
                return columns.flatMap((column) => column.availability);
            }

            return calendars.flatMap(
                (calendar) => calendar.availability.filter(
                    ({ dayOfWeek }) => dayOfWeek === weekDay,
                ),
            );
        },
        [calendars, columns, weekDay],
    );

    const calendarsList = useMemo(
        () => {
            if (columns) {
                return columns.map((column) => (
                    <CalendarItem
                        key={column.id}
                        color={column.color}
                        getEventColor={column.getEventColor}
                        events={column.events}
                        selectedEvent={selectedEvent}
                        availability={column.availability}
                        onEventClick={onEventClick}
                        onAvailableSpaceClick={handleAvailableSpaceClick(
                            column.calendar || calendars[0],
                        )}
                        timeZone={timeZone}
                    />
                ));
            }

            return calendars.map(
                (calendar) => {
                    const calendarEvents = events.filter(
                        (event) => event.calendarId === calendar.id,
                    );
                    return (
                        <CalendarItem
                            key={calendar.id}
                            color={calendar.color}
                            events={calendarEvents}
                            selectedEvent={selectedEvent}
                            availability={calendar.availability.filter(
                                ({ dayOfWeek }) => dayOfWeek === weekDay,
                            )}
                            onEventClick={onEventClick}
                            onAvailableSpaceClick={handleAvailableSpaceClick(calendar)}
                            timeZone={timeZone}
                        />
                    );
                },
            );
        },
        [
            calendars,
            columns,
            events,
            handleAvailableSpaceClick,
            onEventClick,
            selectedEvent,
            timeZone,
            weekDay,
        ],
    );

    const gridLines = useMemo(
        () => Array.from(
            Array(48),
            (_value, index) => {
                const color = (
                    index % 2 === 0
                        ? DIVIDER
                        : `${DIVIDER}72`
                );
                return <GridLine key={index} color={color} />;
            },
        ),
        [],
    );

    useScrollToMinAvailableTime(scrollRef, getMinAvailableStartAt(visibleAvailability));

    return (
        <Scroll
            ref={scrollRef}
            onScroll={onScroll}
        >
            <Container>
                <Content>
                    <Grid>
                        {gridLines}
                    </Grid>
                    <ClockLine visible={!hideClockLine} />
                    <StyledCalendars>
                        <div />
                        {calendarsList}
                    </StyledCalendars>
                </Content>
            </Container>
        </Scroll>
    );
};

export default Calendars;
